import { useCallback, useEffect, useState } from 'react';
import { equipesApi } from '../../api/resources';
import { _inp } from '../../components/ui/Inputs.jsx';
import { tr, trf } from '../../lib/i18n';
import { C } from '../../theme/palette';

/**
 * Pour le technicien (ou la secrétaire) : « Mon équipe ».
 *
 * <p>Il crée ou règle lui-même son équipe : il prend les employés et les
 * chantiers qui n'ont pas d'équipe. Ceux d'une autre équipe paraissent en
 * gris : seuls le patron ou la direction les changent d'équipe. Il reçoit
 * aussi les équipes que le patron ou le directeur lui proposent (« Marc, tu
 * prends Kevin, Pedro et Luca pour la Villa Martin ») et les crée d'un toucher.
 */

const styleTitre = { fontSize: 12, fontWeight: 800, color: C.grey, letterSpacing: 0.5 };
const styleCarte = {
  background: C.white,
  border: '1px solid ' + C.border,
  borderRadius: 14,
  padding: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

function Puce({ on, onClick, children, desactive }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      disabled={desactive}
      onClick={onClick}
      style={{
        padding: '9px 12px',
        minHeight: 40,
        borderRadius: 99,
        border: on ? 'none' : '1px solid ' + C.border,
        background: desactive ? '#ECEDF0' : on ? C.teal : C.white,
        color: desactive ? '#8A90A0' : on ? C.white : C.ink,
        fontSize: 13,
        fontWeight: 700,
        cursor: desactive ? 'default' : 'pointer',
      }}
    >
      {on ? '✓ ' : ''}
      {children}
    </button>
  );
}

const prenom = (nom) => String(nom || '').split(' ')[0];

/** Une équipe proposée : créer d'un toucher, ou retirer quelqu'un avant. */
function Proposition({ p, onFini }) {
  const [retenus, setRetenus] = useState(() => (p.employes || []).map((e) => e.id));
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const agit = async (fn) => {
    setEnvoi(true);
    setErreur('');
    try {
      await fn();
      onFini();
    } catch (e) {
      setErreur(e?.message || tr("L'équipe n'a pas pu être enregistrée."));
      setEnvoi(false);
    }
  };
  return (
    <div style={{ ...styleCarte, border: '2px solid ' + C.teal }} data-proposition-equipe>
      <div style={{ fontSize: 12, color: C.grey }}>
        {trf('📨 {0} te propose une équipe', p.parNom || tr('Le bureau'))}
      </div>
      {p.message && <div style={{ fontSize: 14, lineHeight: 1.45 }}>{p.message}</div>}
      {p.chantierNom && (
        <div style={{ fontSize: 13, fontWeight: 700 }}>
          🏗 {trf('Chantier : {0}', p.chantierNom)}
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {(p.employes || []).map((e) => (
          <Puce
            key={e.id}
            on={retenus.includes(e.id)}
            onClick={() =>
              setRetenus((r) => (r.includes(e.id) ? r.filter((x) => x !== e.id) : [...r, e.id]))
            }
          >
            {prenom(e.nom)}
          </Puce>
        ))}
      </div>
      {erreur && (
        <div role="alert" style={{ fontSize: 12.5, color: C.red }}>
          {erreur}
        </div>
      )}
      <button
        type="button"
        disabled={envoi}
        onClick={() => agit(() => equipesApi.accepte(p.id, retenus))}
        style={{
          border: 'none',
          background: C.green,
          color: C.white,
          borderRadius: 12,
          padding: 13,
          fontSize: 14.5,
          fontWeight: 800,
          cursor: 'pointer',
        }}
      >
        {trf('Créer mon équipe avec eux ({0})', retenus.length)}
      </button>
      <button
        type="button"
        disabled={envoi}
        onClick={() => agit(() => equipesApi.ecarte(p.id))}
        style={{
          border: '1px solid ' + C.border,
          background: C.white,
          borderRadius: 12,
          padding: 10,
          fontSize: 13,
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        {tr('Écarter')}
      </button>
    </div>
  );
}

/**
 * @param {object} props
 * @param {object[]} props.equipes l'équipe que je dirige (useEquipes)
 * @param {() => void} props.onChange relit les équipes et les listes
 */
export function MonEquipe({ equipes = [], onChange }) {
  const mienne = equipes[0] || null;
  const [propositions, setPropositions] = useState([]);
  const [edite, setEdite] = useState(false);
  const [candidats, setCandidats] = useState(null);
  const [f, setF] = useState({ nom: '', employeIds: [], chantierIds: [] });
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');

  const relis = useCallback(() => {
    Promise.resolve()
      .then(() => equipesApi.propositions())
      .then((l) => setPropositions(Array.isArray(l) ? l : []))
      .catch(() => {});
  }, []);
  useEffect(() => {
    relis();
  }, [relis]);

  const ouvre = async () => {
    setErreur('');
    try {
      const c = await equipesApi.candidats();
      setCandidats(c || { employes: [], chantiers: [] });
      setF({
        nom: mienne?.nom || '',
        employeIds: (c?.employes || []).filter((e) => e.mienne).map((e) => e.id),
        chantierIds: (c?.chantiers || []).filter((x) => x.mienne).map((x) => x.id),
      });
      setEdite(true);
    } catch (e) {
      setErreur(e?.message || tr("Les équipes n'ont pas pu être chargées."));
    }
  };

  const bascule = (cle, id) =>
    setF((p) => ({
      ...p,
      [cle]: p[cle].includes(id) ? p[cle].filter((x) => x !== id) : [...p[cle], id],
    }));

  const enregistre = async () => {
    if (!f.nom.trim()) {
      setErreur(tr("Donne un nom à l'équipe."));
      return;
    }
    setEnvoi(true);
    setErreur('');
    try {
      await equipesApi.regleLaMienne({ ...f, nom: f.nom.trim() });
      setEdite(false);
      onChange?.();
    } catch (e) {
      setErreur(e?.message || tr("L'équipe n'a pas pu être enregistrée."));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
      {propositions.map((p) => (
        <Proposition
          key={p.id}
          p={p}
          onFini={() => {
            relis();
            onChange?.();
          }}
        />
      ))}
      {!edite && (
        <button
          type="button"
          onClick={ouvre}
          style={{
            ...styleCarte,
            textAlign: 'left',
            cursor: 'pointer',
            font: 'inherit',
          }}
        >
          <span style={{ fontSize: 14.5, fontWeight: 800, color: C.ink }}>
            👥 {mienne ? mienne.nom : tr('Créer mon équipe')}
          </span>
          <span style={{ fontSize: 12.5, color: C.grey }}>
            {mienne
              ? trf(
                  '{0} employé(s) · {1} chantier(s) — toucher pour régler',
                  (mienne.employeIds || []).length,
                  (mienne.chantierIds || []).length,
                )
              : tr(
                  'Choisis tes employés et tes chantiers. Le patron voit ton équipe et peut la changer.',
                )}
          </span>
        </button>
      )}
      {edite && candidats && (
        <div style={styleCarte}>
          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              fontSize: 12,
              fontWeight: 700,
              color: C.grey,
            }}
          >
            {tr('Nom de mon équipe')}
            <input
              value={f.nom}
              onChange={(e) => setF((p) => ({ ...p, nom: e.target.value }))}
              placeholder={tr('Ex. : Équipe de Marc')}
              maxLength={120}
              style={{ ..._inp, fontSize: 14, minHeight: 44, boxSizing: 'border-box' }}
            />
          </label>
          <div style={styleTitre}>{tr('MES EMPLOYÉS')}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {(candidats.employes || []).map((e) => (
              <Puce
                key={e.id}
                on={f.employeIds.includes(e.id)}
                desactive={!!e.equipe}
                onClick={() => bascule('employeIds', e.id)}
              >
                {prenom(e.nom)}
                {e.equipe ? ' · ' + e.equipe : ''}
              </Puce>
            ))}
          </div>
          {(candidats.employes || []).some((e) => e.equipe) && (
            <div style={{ fontSize: 12, color: C.grey, lineHeight: 1.45 }}>
              {tr(
                'Ceux d’une autre équipe : seuls le patron ou la direction les changent d’équipe.',
              )}
            </div>
          )}
          <div style={styleTitre}>{tr('MES CHANTIERS')}</div>
          {(candidats.chantiers || []).map((x) => (
            <label
              key={x.id}
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'center',
                fontSize: 14,
                color: x.equipe ? '#8A90A0' : C.ink,
              }}
            >
              <input
                type="checkbox"
                disabled={!!x.equipe}
                checked={f.chantierIds.includes(x.id)}
                onChange={() => bascule('chantierIds', x.id)}
                style={{ width: 20, height: 20 }}
              />
              {x.nom}
              {x.equipe ? ' · ' + x.equipe : ''}
            </label>
          ))}
          {erreur && (
            <div role="alert" style={{ fontSize: 12.5, color: C.red }}>
              {erreur}
            </div>
          )}
          <button
            type="button"
            onClick={enregistre}
            disabled={envoi}
            style={{
              border: 'none',
              background: C.green,
              color: C.white,
              borderRadius: 12,
              padding: 14,
              fontSize: 15,
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            {envoi ? tr('Enregistrement…') : tr('Enregistrer mon équipe')}
          </button>
          <button
            type="button"
            onClick={() => setEdite(false)}
            style={{
              border: '1px solid ' + C.border,
              background: C.white,
              borderRadius: 12,
              padding: 10,
              fontWeight: 700,
            }}
          >
            {tr('Annuler')}
          </button>
        </div>
      )}
      {!edite && erreur && (
        <div role="alert" style={{ fontSize: 12.5, color: C.red }}>
          {erreur}
        </div>
      )}
    </div>
  );
}
